import React, { memo, useEffect, useMemo } from 'react';
import { ActionType } from '@src/components/generated/graphql';
import { useUpdateNodeInternals, useStore } from '@xyflow/react';
import { buildHandleId } from '../../lib/utils';
import {
  HANDLES_TYPES,
  SUPPORTED_ACTIONS_PREVIEW,
  NODES_TYPES,
} from '../../types';
import { Button } from 'antd';
import { ACTION_PREVIEW_EVENT } from '../actions-preview/ActionPreview';
import CustomNode from './CustomNode';
import HandlesWrapper from '../HandlesWrapper';
import styles from '../../timeline-node-manager.module.css';
import type { HandleItem } from '../HandlesWrapper';
import { getActionNodeLabel } from './nodeLabelUtils';
import * as THREE from 'three';
import {
  ASSET_CATEGORY_TYPES,
  ASSET_PREVIEW_TYPES,
} from '../../../../../types/assetTypes';

interface Asset {
  id: string;
  name: string;
  url: string;
  type?: string;
  previewImage?: string;
  ref: {
    current: {
      uuid: string;
    };
  };
}

interface SceneAssetProps {
  position?: THREE.Vector3;
  scale?: THREE.Vector3;
  rotation?: THREE.Euler;
  instanceId: string;
  asset: Asset;
}

interface ReactElement {
  props: SceneAssetProps;
  type: React.ComponentType<any>;
  key: string;
}

interface ActionNodeProps {
  id: string;
  data: any;
  type: NODES_TYPES;
  selected: boolean;
  selectableAssets?: ReactElement[];
}

const ActionNode = ({
  id,
  data,
  type,
  selected,
  selectableAssets = [],
}: ActionNodeProps) => {
  const updateNodeInternals = useUpdateNodeInternals();
  const edges = useStore((state) => state.edges);
  const nodes = useStore((state) => state.nodes);

  const nodeLabel = useMemo(() => {
    return getActionNodeLabel(data);
  }, [data.type, data.params?.flyPresetType]);

  const targetInfo = useMemo(() => {
    const actionEdge = edges.find((edge) => edge.source === id);
    if (!actionEdge) return null;

    const targetNode = nodes.find(
      (node) =>
        node.id === actionEdge.target && node.type === NODES_TYPES.TARGET
    );
    if (!targetNode) return null;

    return {
      objectId: targetNode.data.objectId as string,
      previewImage: targetNode.data.previewImage as string,
    };
  }, [id, edges, nodes]);

  const targetPreviewImage = useMemo(() => {
    if (!targetInfo?.objectId || !selectableAssets?.length) {
      return targetInfo?.previewImage || null;
    }

    const selectedAsset = selectableAssets.find((asset) => {
      const assetId = asset.props.asset?.id;
      const assetUuid = asset.props.asset?.ref?.current?.uuid;
      const instanceId = asset.props.instanceId;
      return (
        assetId === targetInfo.objectId ||
        assetUuid === targetInfo.objectId ||
        instanceId === targetInfo.objectId
      );
    });

    if (!selectedAsset?.props?.asset) {
      return targetInfo.previewImage || null;
    }

    const assetData = selectedAsset.props.asset;

    if (
      (assetData.type === ASSET_CATEGORY_TYPES.MODELS ||
        assetData.type === 'OTHER') &&
      assetData.url &&
      assetData.url.includes('/assets/')
    ) {
      return ASSET_PREVIEW_TYPES.MODEL_PREVIEW;
    }

    return assetData.previewImage || assetData.url;
  }, [targetInfo, selectableAssets]);

  const npcPreviewImage = useMemo(() => {
    if (data.type === ActionType.SpawnNpc) {
      return data.params?.previewImage || '🤖';
    }
    return null;
  }, [data.type, data.params?.previewImage]);

  const targets: HandleItem[] = useMemo(() => {
    if (
      data.type === ActionType.Move ||
      data.type === ActionType.Rotate ||
      data.type === ActionType.Scale ||
      data.type === ActionType.Show ||
      data.type === ActionType.Hide ||
      data.type === ActionType.PlayAnimation ||
      (data.type === ActionType.ShowModal && data.params?.showModelPreview) ||
      (data.type === ActionType.SwitchBetweenCameras &&
        data.params?.enableLookAtTarget) ||
      (data.type === ActionType.MoveMainCamera &&
        data.params?.enableLookAtTarget)
    ) {
      return [
        {
          id: buildHandleId({
            type: 'output',
            handleType: HANDLES_TYPES.ACTION,
            nodeId: id,
          }),
          label: 'Target Object',
        },
      ];
    }

    return [];
  }, [data]);

  const sources: HandleItem[] = useMemo(() => {
    if (data?.isOnComplete === true) {
      return [
        {
          label: 'Collectible',
          id: buildHandleId({
            type: 'input',
            handleType: HANDLES_TYPES.COLLECTIBLE,
            nodeId: id,
          }),
        },
      ];
    }

    if (data?.isOnComplete === false) {
      return [
        {
          label: 'Event',
          id: buildHandleId({
            type: 'input',
            handleType: HANDLES_TYPES.EVENT,
            nodeId: id,
          }),
        },
      ];
    }

    return [
      {
        label: 'Event',
        id: buildHandleId({
          type: 'input',
          handleType: HANDLES_TYPES.EVENT,
          nodeId: id,
        }),
      },
      {
        label: 'Collectible',
        id: buildHandleId({
          type: 'input',
          handleType: HANDLES_TYPES.COLLECTIBLE,
          nodeId: id,
        }),
      },
    ];
  }, [data]);

  function onOpenPreview() {
    window.dispatchEvent(
      new CustomEvent(ACTION_PREVIEW_EVENT, {
        detail: {
          action: data,
        },
      })
    );
  }

  useEffect(() => {
    updateNodeInternals(id);
  }, [targets.length, sources.length]);

  return (
    <CustomNode
      id={id}
      data={data}
      label={nodeLabel}
      type={type}
      containerClassname={styles.conditionNode}
      selected={selected}
    >
      {SUPPORTED_ACTIONS_PREVIEW.includes(data.type) && (
        <Button onClick={onOpenPreview} className={styles.previewActionButton}>
          Preview
        </Button>
      )}
      <HandlesWrapper sources={sources} targets={targets} />
      {targetInfo?.objectId && (
        <>
          {targetPreviewImage === ASSET_PREVIEW_TYPES.MODEL_PREVIEW ? (
            <div className={styles.targetPreviewContainer}>
              <div className={styles.modelPreview}>
                <div className={styles.modelIcon}>📦</div>
              </div>
            </div>
          ) : targetPreviewImage ? (
            <div className={styles.targetPreviewContainer}>
              <img
                src={targetPreviewImage}
                alt='Target preview'
                className={styles.targetPreviewImage}
                onError={(e) => {
                  console.error('Error loading preview image:', e);
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          ) : (
            <span className={styles.targetId}>{targetInfo.objectId}</span>
          )}
        </>
      )}
      {npcPreviewImage && (
        <div className={styles.targetPreviewContainer}>
          {npcPreviewImage.startsWith('http') ? (
            <img
              src={npcPreviewImage}
              alt='NPC preview'
              className={styles.targetPreviewImage}
              onError={(e) => {
                console.error('Error loading NPC preview image:', e);
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <div className={styles.modelPreview}>
              <div className={styles.modelIcon}>{npcPreviewImage}</div>
            </div>
          )}
        </div>
      )}
    </CustomNode>
  );
};

export default memo(ActionNode);
